import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, Subscription, of, retry } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  Abbreviation,
  AbbreviationStatus,
  CrewGroup,
  CrewMemberType,
  DaySchedule,
  IndicatorType,
  JobIndicator,
  ProductionEvent,
  RemovedBlock,
  ScheduleButtonType,
  resolveCapabilityScope,
} from '@groundworks/ngx-groundworks-ui';
import {
  CalendarCrewScheduleInfo,
  CalendarEventInfo,
  PermitsIndicatorStatus,
  SpecialNeedStatus,
  UtilityStatus,
  WeeklyScheduleInfo,
} from '../sdk/sdk';
import { CalendarRole, DEFAULT_CALENDAR_ROLES } from '../models/calendar-role';
import { formatLocalIsoDate, formatUtcIsoDate, parseLocalIsoDate } from '../utils/iso-date';
import { BranchService } from './branch.service';
import { CalendarService } from './calendar.service';
import { ConfigService } from '../config.service';
import { ServiceLineService } from './service-line.service';

const API_BUTTON_REMOVE = 'remove';
const API_STATUS_AWAY = 'away';
const API_STATUS_VISITING = 'visiting';


export interface EventMetadata {
  opportunityId?: string;
  foremanId: string;
  projectId: string;
  lastDate: Date;
  installDateIds: string[];
}

@Injectable({ providedIn: 'root' })
export class CrewScheduleStore {
  private readonly calendarService = inject(CalendarService);
  private readonly branchService = inject(BranchService);
  private readonly serviceLineService = inject(ServiceLineService);
  private readonly configService = inject(ConfigService);

  private readonly _crewGroups = signal<CrewGroup[] | null>(null);
  readonly crewGroups = this._crewGroups.asReadonly();
  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();
  private readonly _isError = signal(false);
  readonly isError = this._isError.asReadonly();
  private readonly _scrollToCrewGroupId = signal<string | null>(null);
  readonly scrollToCrewGroupId = this._scrollToCrewGroupId.asReadonly();
  private readonly _warnings = signal<string[]>([]);
  readonly warnings = this._warnings.asReadonly();
  private readonly _deletionReasons = signal<string[] | null>(null);
  readonly deletionReasons = this._deletionReasons.asReadonly();
  readonly legendItems = signal(this.serviceLineService.getLegendItems()).asReadonly();

  private initSubscription: Subscription | null = null;
  private loadSubscription: Subscription | null = null;
  private destroyRef: DestroyRef | null = null;

  private readonly _allCrewGroups = signal<CrewGroup[] | null>(null);
  readonly allCrewGroups = this._allCrewGroups.asReadonly();
  private readonly _startDate = signal<Date | null>(null);
  readonly startDate = this._startDate.asReadonly();
  private readonly _endDate = signal<Date | null>(null);
  readonly endDate = this._endDate.asReadonly();
  private readonly _selectedForemenIds = signal<string[]>([]);
  private readonly _selectedRoles = signal<CalendarRole[]>([...DEFAULT_CALENDAR_ROLES]);
  private readonly _selectedAbilities = signal<string[]>([]);

  /** Role classification per crew, keyed by foremanUserId — derived from the
   * API `department` field at map-time. Visiting crews are not present here
   * (they bypass the role gate). */
  private readonly crewRoles = new Map<string, CalendarRole>();
  /** canInstallProjectTypes per crew, keyed by foremanUserId. A missing entry
   * means the crew had no projectCapability — used to fail the ability gate
   * deterministically rather than silently admit such crews. */
  private readonly crewAbilities = new Map<string, readonly string[]>();

  private readonly eventMetadata = new Map<string, EventMetadata>();

  setScrollToCrewGroupId(id: string | null): void {
    this._scrollToCrewGroupId.set(id);
  }

  setIsLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }

  getOpportunityId(eventKey: string): string | undefined {
    return this.eventMetadata.get(eventKey)?.opportunityId;
  }

  getForemanId(eventKey: string): string | undefined {
    return this.eventMetadata.get(eventKey)?.foremanId;
  }

  getProjectId(eventKey: string): string | undefined {
    return this.eventMetadata.get(eventKey)?.projectId;
  }

  getLastDate(eventKey: string): Date | undefined {
    return this.eventMetadata.get(eventKey)?.lastDate;
  }

  getInstallDateIds(eventKey: string): string[] | undefined {
    return this.eventMetadata.get(eventKey)?.installDateIds;
  }

  setLastDate(eventKey: string, date: Date): void {
    const meta = this.eventMetadata.get(eventKey);
    if (meta) {
      meta.lastDate = date;
    } else {
      console.warn('setLastDate: no metadata found for event', eventKey);
    }
  }

  pushInstallDateId(eventKey: string, id: string): void {
    const meta = this.eventMetadata.get(eventKey);
    if (meta) {
      meta.installDateIds.push(id);
    } else {
      console.warn('pushInstallDateId: no metadata found for event', eventKey);
    }
  }

  popInstallDateId(eventKey: string): void {
    const meta = this.eventMetadata.get(eventKey);
    if (meta) {
      meta.installDateIds.pop();
    } else {
      console.warn('popInstallDateId: no metadata found for event', eventKey);
    }
  }

  initialize(destroyRef: DestroyRef): void {
    this.initSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
    this.destroyRef = destroyRef;

    this._crewGroups.set(null);
    this._allCrewGroups.set(null);
    this._isLoading.set(false);
    this._isError.set(false);
    this._warnings.set([]);
    this._scrollToCrewGroupId.set(null);
    this._deletionReasons.set(null);
    this._startDate.set(null);
    this._endDate.set(null);
    this._selectedForemenIds.set([]);
    this._selectedRoles.set([...DEFAULT_CALENDAR_ROLES]);
    this._selectedAbilities.set([]);

    this.eventMetadata.clear();
    this.crewRoles.clear();
    this.crewAbilities.clear();

    const sub = new Subscription();
    this.initSubscription = sub;

    sub.add(this.branchService
      .getSelectedBranch()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((branch) => {
        if (!branch) {
          this._crewGroups.set(null);
          this._allCrewGroups.set(null);
          return;
        }
        this.loadCrewSchedules();
      }));

    sub.add(this.calendarService
      .getSelectedForemenIds()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((ids) => {
        this._selectedForemenIds.set(ids);
        this.applyForemanFilter();
      }));

    sub.add(this.calendarService
      .getSelectedRoles()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((roles) => {
        this._selectedRoles.set(roles);
        this.applyForemanFilter();
      }));

    sub.add(this.calendarService
      .getSelectedAbilities()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((abilities) => {
        this._selectedAbilities.set(abilities);
        this.applyForemanFilter();
      }));

    sub.add(this.calendarService.getDeletionReasons()
      .pipe(
        retry(2),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe({
        next: (reasons) => { this._deletionReasons.set(reasons); },
        error: (err: unknown) => {
          console.error('Failed to load deletion reasons:', err);
          this._deletionReasons.set(null);
          this._warnings.update((w) => [...w, 'Deletion reasons could not be loaded — removing availability may not work']);
        },
      }));
  }

  setWeekDates(dates: { date: Date }[]): void {
    if (dates.length === 0) return;
    const startDate = dates[0].date;
    const endDate = dates.at(-1)!.date;
    this._startDate.set(startDate);
    this._endDate.set(endDate);
    this.calendarService.setDateRange(startDate, endDate);
    this.loadCrewSchedules();
  }

  loadCrewSchedules(
    scrollToForeman?: string,
    options?: { suppressErrorState?: boolean },
  ): Observable<void> {
    const branchId = this.branchService.getCurrentSelectedBranch()?.id;
    const startDate = this._startDate();
    const endDate = this._endDate();
    if (!branchId || !startDate || !endDate) {
      console.debug('loadCrewSchedules skipped: missing branch or date range', {
        branchId,
        startDate,
        endDate,
      });
      return of(undefined);
    }

    this.loadSubscription?.unsubscribe();

    const pendingScroll = scrollToForeman ?? this._scrollToCrewGroupId();

    this._isLoading.set(true);
    this._isError.set(false);
    this._scrollToCrewGroupId.set(null);

    const done$ = new Subject<void>();
    let settled = false;

    let obs$ = this.calendarService.getCrewSchedules(branchId, startDate, endDate);
    if (this.destroyRef) {
      obs$ = obs$.pipe(takeUntilDestroyed(this.destroyRef));
    }

    this.loadSubscription = obs$
      .pipe(
        finalize(() => {
          if (!settled) {
            settled = true;
            done$.next();
            done$.complete();
          }
        }),
      )
      .subscribe({
        next: (result) => {
          this._allCrewGroups.set(this.mapToCrewGroups(result.schedules ?? []));
          this._warnings.set(result.warnings ?? []);
          this.applyForemanFilter();
          this._isLoading.set(false);
          if (pendingScroll) {
            this._scrollToCrewGroupId.set(pendingScroll);
          }
          settled = true;
          done$.next();
          done$.complete();
        },
        error: (err: unknown) => {
          console.error('Failed to load crew schedules', { branchId, startDate, endDate, err });
          this._isLoading.set(false);
          if (!options?.suppressErrorState) {
            this._isError.set(true);
          }
          this._warnings.set([]);
          settled = true;
          done$.error(err);
        },
      });

    return done$.asObservable();
  }

  applyForemanFilter(): void {
    const all = this._allCrewGroups();
    if (!all) {
      this._crewGroups.set(null);
      return;
    }
    const ids = this._selectedForemenIds();
    const roles = this._selectedRoles();
    const abilities = this._selectedAbilities();

    // Filter composition:
    //   - Ability gate is a hard eligibility check, like branch — a crew that
    //     cannot install a selected ProjectType is hidden regardless of any
    //     other signal.
    //   - Within the ability-eligible set: a crew with data in the visible
    //     range (active availability, default availability, or any install)
    //     bypasses the role + id filters. A crew without data falls through
    //     to the role + id gates.
    //   - Empty role selection still hides every crew without data.
    //   - Branch filter is the outer ring (applied upstream, not here).
    this._crewGroups.set(
      all.filter((group) =>
        this.passesAbilityGate(group, abilities)
        && (
          this.hasDataInVisibleRange(group)
          || (
            this.passesRoleGate(group, roles)
            && this.passesIdGate(group, ids)
          )
        ),
      ),
    );
  }

  private hasDataInVisibleRange(group: CrewGroup): boolean {
    // Data signals (any-of):
    //   - day.isAvailable: server-derived flag, true whenever buttonType === REMOVE
    //     (concrete ForemanAvailability OR DefaultForemanAvailability match,
    //     for both home and visiting crews)
    //   - day.events.length > 0: at least one install scheduled
    // Away days (status === AWAY, deletion-with-reason) are NOT data.
    // Removed blocks alone are NOT data.
    for (const day of group.schedule) {
      if (day.isAvailable) return true;
      if (day.events.length > 0) return true;
    }
    return false;
  }

  private passesRoleGate(group: CrewGroup, selectedRoles: readonly CalendarRole[]): boolean {
    const role = this.crewRoles.get(group.id) ?? 'Foreman';
    return selectedRoles.includes(role);
  }

  private passesIdGate(group: CrewGroup, selectedIds: readonly string[]): boolean {
    if (selectedIds.length === 0) return true;
    return selectedIds.includes(group.id);
  }

  private passesAbilityGate(group: CrewGroup, selectedAbilities: readonly string[]): boolean {
    if (selectedAbilities.length === 0) return true;
    const can = this.crewAbilities.get(group.id) ?? [];
    return can.some((ability) => selectedAbilities.includes(ability));
  }

  findDay(foremanUserId: string, date: Date): DaySchedule | undefined {
    const crewGroup = this._allCrewGroups()?.find((cg) => cg.id === foremanUserId);
    if (!crewGroup) return undefined;
    return crewGroup.schedule.find((ds) => ds.date.getTime() === date.getTime());
  }

  /** Create a new CrewGroup reference so the production calendar library detects the change and re-renders. */
  replaceCrewGroupRef(foremanUserId: string): void {
    const all = this._allCrewGroups();
    if (!all) return;
    const index = all.findIndex((cg) => cg.id === foremanUserId);
    if (index === -1) return;
    const updated = [...all];
    updated[index] = { ...updated[index] };
    this._allCrewGroups.set(updated);
    this.applyForemanFilter();
  }

  setDayButtonDisabled(foremanUserId: string, date: Date, disabled: boolean): void {
    this.updateDay(foremanUserId, date, 'setDayButtonDisabled', (day) => ({ ...day, buttonDisabled: disabled }));
  }

  setDayButtonType(foremanUserId: string, date: Date, type: ScheduleButtonType): void {
    this.updateDay(foremanUserId, date, 'setDayButtonType', (day) => ({
      ...day,
      buttonType: type,
      isAvailable: type === ScheduleButtonType.REMOVE,
    }));
  }

  setDayRemovedBlocks(foremanUserId: string, date: Date, blocks: RemovedBlock[]): void {
    this.updateDay(foremanUserId, date, 'setDayRemovedBlocks', (day) => ({ ...day, removedBlocks: blocks }));
  }

  private updateDay(
    foremanUserId: string,
    date: Date,
    caller: string,
    updater: (day: DaySchedule) => DaySchedule,
  ): void {
    const all = this._allCrewGroups();
    if (!all) { console.warn(`${caller}: allCrewGroups is null`); return; }
    const cgIndex = all.findIndex((cg) => cg.id === foremanUserId);
    if (cgIndex === -1) { console.warn(`${caller}: crew group not found`, foremanUserId); return; }
    const crewGroup = all[cgIndex];
    const dayIndex = crewGroup.schedule.findIndex((ds) => ds.date.getTime() === date.getTime());
    if (dayIndex === -1) { console.warn(`${caller}: day not found`, foremanUserId, date); return; }

    const updatedSchedule = [...crewGroup.schedule];
    updatedSchedule[dayIndex] = updater(updatedSchedule[dayIndex]);
    const updatedAll = [...all];
    updatedAll[cgIndex] = { ...crewGroup, schedule: updatedSchedule };
    this._allCrewGroups.set(updatedAll);
    this.applyForemanFilter();
  }

  findCrewGroupIndex(foremanUserId: string): number {
    return this._allCrewGroups()?.findIndex((cg) => cg.id === foremanUserId) ?? -1;
  }

  getCrewGroupAt(index: number): CrewGroup | undefined {
    return this._allCrewGroups()?.[index];
  }

  setCrewGroupAt(index: number, crewGroup: CrewGroup): void {
    const all = this._allCrewGroups();
    if (!all) return;
    if (index < 0 || index >= all.length) {
      console.warn('setCrewGroupAt: index out of bounds', index, 'length', all.length);
      return;
    }
    const updated = [...all];
    updated[index] = crewGroup;
    this._allCrewGroups.set(updated);
    this.applyForemanFilter();
  }

  private mapToCrewGroups(schedules: CalendarCrewScheduleInfo[]): CrewGroup[] {
    this.eventMetadata.clear();
    this.crewRoles.clear();
    this.crewAbilities.clear();

    const mapped = schedules
      .filter((crew) => {
        if (!crew.foremanUserId) {
          console.warn('mapToCrewGroups: crew has no foremanUserId, skipping', crew.foremanName);
          return false;
        }
        return true;
      })
      .map((crew) => {
      const foremanName = crew.foremanName ?? '';
      const schedule = crew.schedule ?? [];
      const foremanUserId = crew.foremanUserId ?? '';

      for (const ws of schedule) {
        const wsDate = CrewScheduleStore.toLocalDate(ws.date);
        for (const evt of ws.events ?? []) {
          const key = CrewScheduleStore.eventKey(foremanUserId, evt.projectId, evt.timeHeader, wsDate);
          const lastSpanDate = new Date(wsDate);
          lastSpanDate.setDate(lastSpanDate.getDate() + Math.max(0, (evt.spanDays ?? 1) - 1));

          // Keys are unique per (foreman, project, timeHeader, anchor date), so each
          // event has exactly one metadata entry. Non-consecutive runs (Mon + Wed)
          // are distinct events with independent lastDate values.
          this.eventMetadata.set(key, {
            opportunityId: evt.opportunityId ?? undefined,
            foremanId: foremanUserId,
            projectId: evt.projectId,
            lastDate: lastSpanDate,
            installDateIds: evt.installDateIds?.length ? [...evt.installDateIds] : [],
          });
        }
      }

      const capability = crew.projectCapability;
      const canInstall = capability?.canInstallProjectTypes ?? [];
      const cannotInstall = capability?.cannotInstallProjectTypes ?? [];
      const capabilityScope = resolveCapabilityScope(canInstall, cannotInstall);

      const isVisiting = crew.type === API_STATUS_VISITING;
      const department = crew.department;
      const section =
        !isVisiting && (department === 'Sub-Contractor' || department === 'Co-Foreman' || department === 'Field Superintendent')
          ? department
          : undefined;

      if (!isVisiting) {
        this.crewRoles.set(foremanUserId, CrewScheduleStore.classifyRole(department));
      }
      // Always set abilities (even when empty) so the gate's set-intersection
      // is the single source of truth. Empty `canInstall` (no capability or
      // empty array) naturally fails any non-empty ability selection.
      this.crewAbilities.set(foremanUserId, [...canInstall]);

      return {
        id: crew.foremanUserId ?? '',
        name: foremanName,
        type: isVisiting ? CrewMemberType.VISITING : CrewMemberType.DEFAULT,
        section,
        avgIvpd: { ivpdAmount: crew.ivpd ?? 0 },
        weeklyInstallAmount: crew.weeklyInstall ?? 0,
        items: canInstall,
        capabilityScope,
        schedule: this.mapToDaySchedules(schedule, foremanUserId),
      };
    });

    const result = [
      ...mapped.filter((g) => g.type !== CrewMemberType.VISITING),
      ...mapped.filter((g) => g.type === CrewMemberType.VISITING),
    ];

    this.calendarService.setAvailableAbilities(this.getDistinctInstallationAbilities());

    return result;
  }

  private mapToDaySchedules(weeklySchedules: WeeklyScheduleInfo[], foremanUserId: string): DaySchedule[] {
    return weeklySchedules.map((ws) => {
      const anchorDate = CrewScheduleStore.toLocalDate(ws.date);
      const buttonType = ws.buttonType === API_BUTTON_REMOVE
        ? ScheduleButtonType.REMOVE
        : ScheduleButtonType.ADD;
      const daySchedule: DaySchedule = {
        date: anchorDate,
        buttonType,
        isAvailable: buttonType === ScheduleButtonType.REMOVE,
        events: (ws.events ?? []).map((evt) => this.mapToProductionEvent(evt, foremanUserId, anchorDate)),
        removedBlocks: (ws.removedBlocks ?? []).map((rb) => ({
          id: rb.id,
          timeHeader: rb.timeHeader ?? 'All Day',
          reason: rb.reason ?? '',
        })),
      };
      if (ws.status != null) {
        const status = ws.status;
        daySchedule.status = CrewScheduleStore.mapDayStatus(status);
        daySchedule.buttonDisabled = status === API_STATUS_AWAY;
      }
      return daySchedule;
    });
  }

  private mapToProductionEvent(event: CalendarEventInfo, foremanUserId: string, anchorDate: Date): ProductionEvent {
    const indicators: JobIndicator[] = [];
    if (event.totalProjectCountOnJob && event.totalProjectCountOnJob > 1) {
      indicators.push({ type: IndicatorType.MULTI_PROJECT, value: `${event.projectNumber}/${event.totalProjectCountOnJob}`, label: 'Multi-Project' });
    }
    if (event.isLastDayOnJob) {
      indicators.push({ type: IndicatorType.JOB_LAST_DAY, label: 'Last Day' });
    } else if (event.isLastDayOnProject) {
      indicators.push({ type: IndicatorType.LAST_DAY, label: 'Last Day' });
    }

    const abbreviations: Abbreviation[] = event.isPunchwork ? [] : [
      { label: 'R', status: event.isReviewed ? AbbreviationStatus.READY : AbbreviationStatus.NOT_READY },
      { label: 'C', status: event.isConfirmationCallComplete ? AbbreviationStatus.READY : AbbreviationStatus.NOT_READY },
    ];

    if (event.utilityStatus != null && !event.isPunchwork) {
      abbreviations.push({ label: 'U', status: CrewScheduleStore.mapUtilityStatus(event.utilityStatus) });
    }

    // MOD-2003 — Permits "P" sits between Utilities and Spec. Needs per AC metrics ordering.
    if (event.permitsStatus != null && !event.isPunchwork) {
      abbreviations.push({ label: 'P', status: CrewScheduleStore.mapPermitsStatus(event.permitsStatus) });
    }

    if (event.specialNeedStatus != null && !event.isPunchwork) {
      abbreviations.push({ label: 'S', status: CrewScheduleStore.mapSpecialNeedStatus(event.specialNeedStatus) });
    }

    return {
      id: CrewScheduleStore.eventKey(foremanUserId, event.projectId ?? '', event.timeHeader, anchorDate),
      timeHeader: event.timeHeader ?? 'All Day',
      eventType: event.projectType ?? 'Unknown',
      subEventType: event.linkedProjectType ?? undefined,
      cost: event.netAmount ?? 0,
      customerName: event.customerName ?? '',
      location: event.address ?? '',
      abbreviations,
      variant: event.serviceLineId ? this.serviceLineService.getVariantById(event.serviceLineId) : 'default',
      indicators,
      spanDays: event.spanDays > 1 ? event.spanDays : undefined,
      isBestFit: event.isBestFit ?? false,
      isPunchwork: event.isPunchwork ?? false,
      footer: event.isPunchwork || !this.configService.enableCrew
        // Punchwork never shows crew; when the crew feature is off (MOD-3381) the
        // per-day crew count is omitted entirely (no "Crew 0" residue), leaving the
        // install amount intact.
        ? (event.dailyInstallAmounts ?? []).map((amount) => ({
            text: CrewScheduleStore.formatCurrency(amount),
          }))
        : (event.dailyInstallAmounts ?? []).map((amount, i) => ({
            text: `${CrewScheduleStore.formatCurrency(amount)} / Crew x${event.dailyCrewCounts?.[i] ?? 0}`,
          })),
    };
  }

  static mapDayStatus(status: string | null | undefined): CrewMemberType {
    switch (status) {
      case API_STATUS_AWAY: return CrewMemberType.AWAY;
      case API_STATUS_VISITING: return CrewMemberType.VISITING;
      case null:
      case undefined:
        return CrewMemberType.DEFAULT;
      default:
        console.warn(`mapDayStatus: unknown value "${status}", defaulting to DEFAULT`);
        return CrewMemberType.DEFAULT;
    }
  }

  static eventKey(
    foremanUserId: string,
    projectId: string,
    timeHeader: string | undefined,
    anchorDate: Date | string,
  ): string {
    const dateStr = typeof anchorDate === 'string' ? anchorDate : formatLocalIsoDate(anchorDate);
    return `${foremanUserId}:${projectId}:${timeHeader ?? 'All Day'}:${dateStr}`;
  }

  /** Convert a UTC-midnight Date (from DateOnly serialization) to local midnight. */
  static toLocalDate(date: Date | string): Date {
    return parseLocalIsoDate(formatUtcIsoDate(date))!;
  }

  static formatCurrency(amount: number): string {
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /**
   * Classify a crew's role from the API `department` free-text field.
   * Mirrors the section logic in `mapToCrewGroups` so role and section stay
   * in sync. Default (null/unknown department) is 'Foreman'.
   */
  static classifyRole(department: string | undefined | null): CalendarRole {
    if (department === 'Sub-Contractor') return 'Sub-Contractor';
    if (department === 'Co-Foreman') return 'Co-Foreman';
    if (department === 'Field Superintendent') return 'Field Superintendent';
    return 'Foreman';
  }

  static mapSpecialNeedStatus(status: SpecialNeedStatus): AbbreviationStatus {
    switch (status) {
      case SpecialNeedStatus.AllComplete: return AbbreviationStatus.READY;
      case SpecialNeedStatus.Overdue: return AbbreviationStatus.OVERDUE;
      default: return AbbreviationStatus.NOT_READY;
    }
  }

  static mapUtilityStatus(status: UtilityStatus): AbbreviationStatus {
    switch (status) {
      case UtilityStatus.AllComplete: return AbbreviationStatus.READY;
      case UtilityStatus.Overdue: return AbbreviationStatus.OVERDUE;
      default: return AbbreviationStatus.NOT_READY;
    }
  }

  static mapPermitsStatus(status: PermitsIndicatorStatus): AbbreviationStatus {
    switch (status) {
      case PermitsIndicatorStatus.AllApproved: return AbbreviationStatus.READY;
      case PermitsIndicatorStatus.Overdue: return AbbreviationStatus.OVERDUE;
      default: return AbbreviationStatus.NOT_READY;
    }
  }

  /** Distinct installation-ability project types observed across all loaded
   * crews — used as the source list for the "View by Installation Ability"
   * selector. Sorted alphabetically for stable display. */
  getDistinctInstallationAbilities(): string[] {
    const distinct = new Set<string>();
    for (const abilities of this.crewAbilities.values()) {
      for (const ability of abilities) {
        if (ability) distinct.add(ability);
      }
    }
    return [...distinct].sort((a, b) => a.localeCompare(b));
  }

}
